import { useCallback, useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import styled from "@emotion/styled";
import { Flex, Text } from "styles/utility-components";

import { FileUploadStatus } from "components/bid_module/types";
import UploadDocumentsForm from "components/bid_module/UploadDocumentsForm";
import Button from "components/ui/button/Button";
import toast from "components/ui/toast/Toast";
import { useUploadCustomerDocuments } from "lib/hooks/api/customer_documents/useUploadCustomerDocuments";
import UIcon from "lib/icons/untitled_ui/UIcon";
import { CustomerDocumentType } from "lib/types/graphQLEnums";

type Props = {
  includeDescription?: boolean;
  uiPath: string | undefined; // Only present if we're already in a folder
  onClose?: () => void;
};
export default function UploadContentLibrary({
  uiPath,
  includeDescription = true,
  onClose,
}: Props) {
  const { control, watch, reset } = useForm<{ files: File[] }>({
    defaultValues: { files: [] },
    mode: "onChange",
  });

  const files = watch("files");

  const [uploadStatuses, setUploadStatuses] = useState<FileUploadStatus[]>([]);
  const [filesProcessing, setFilesProcessing] = useState<boolean>(false);
  const toastIdRef = useRef<string | number | null>(null);

  const closeToast = () => {
    if (toastIdRef.current !== null) {
      // Dismiss toast when x becomes false
      toast.dismiss(toastIdRef.current);
      toastIdRef.current = null;
    }
  };

  const onFinish = useCallback(() => {
    reset({ files: [] });
    onClose?.();
  }, [reset, onClose]);

  /**
   * Flexible utility function which updates the uploadStatus of a file in
   * the upload queue
   */
  const updateFileStatus = ({
    fileName,
    documentId,
    updates,
  }: {
    fileName: string | null | undefined;
    documentId: string | null | undefined;
    updates: Partial<FileUploadStatus>;
  }) => {
    setUploadStatuses((prevStatuses) =>
      prevStatuses.map((status) => {
        const matchesByDocumentId = documentId && status.documentId === documentId;
        const matchesByFileName = !documentId && fileName && status.file.name === fileName;

        if (matchesByDocumentId || matchesByFileName) {
          return { ...status, ...updates };
        }

        return status;
      }),
    );
  };

  // Check if all files are processed whenever uploadStatus changes
  useEffect(() => {
    if (files.length === 0 || uploadStatuses.length === 0) {
      return;
    }

    const allFilesFinished = uploadStatuses.every((fileStatus) => fileStatus.isFinished);

    if (allFilesFinished) {
      setFilesProcessing(false);
      closeToast();
      // Close modal & reset files if all files are processed
      onFinish();
    }
  }, [files.length, onClose, onFinish, uploadStatuses]);

  const { mutate: uploadFiles, isLoading: isUploadingFiles } = useUploadCustomerDocuments({
    files,
    documentType: CustomerDocumentType.ContentLibrary,
    uploadStatuses,
    updateFileStatus,
    filesProcessing,
    uiPath,
  });

  const isLoading = isUploadingFiles || filesProcessing;

  return (
    <UploadDocumentsContainer aria-label="Upload Content library documents">
      <UploadDocumentsForm
        name="files"
        control={control}
        uploadFileStatus={uploadStatuses}
        setUploadFileStatus={setUploadStatuses}
        isLoading={isLoading}
        includeDescription={includeDescription}
        maxHeight={150}
      />
      <ButtonContainer>
        <Button buttonType="secondary" onClick={onFinish}>
          Close
        </Button>
        <Button
          disabled={files.length === 0}
          loading={isLoading}
          onClick={async () => {
            setFilesProcessing(true);
            uploadFiles();
            toastIdRef.current = toast.info(
              <Text>
                We're processing your documents.\ You can leave this page and we'll email you when
                your documents are ready
              </Text>,
              {
                duration: Infinity, // Keep until manually dismissed
                icon: <UIcon icon="infoCircle" size={16} color="sysInfoDefault" />,
              },
            );
          }}
        >
          Add to library
        </Button>
      </ButtonContainer>
    </UploadDocumentsContainer>
  );
}

const UploadDocumentsContainer = styled(Flex)({
  gap: 12,
  flexDirection: "column",
  width: 600,
});

const ButtonContainer = styled(Flex)({
  justifyContent: "flex-end",
  gap: 16,
  marginTop: 16,
});
